let deliveryItemDraftCounter = 0;

const nextDraftId = () => {
  deliveryItemDraftCounter += 1;
  return `delivery-item-${deliveryItemDraftCounter}`;
};

const normalizeText = (value) => String(value || '').trim();

const normalizeDraftNumberInput = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  const normalized = String(value).trim();
  if (!normalized) {
    return '';
  }
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) {
    return '';
  }
  return String(numeric);
};

const parsePositiveNumber = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return null;
  }
  return numeric;
};

const parseQuantity = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 1;
  }
  return Math.max(1, Math.round(numeric));
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const roundWeight = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 10000) / 10000;

export const createEmptyDeliveryItem = (overrides = {}) => ({
  row_id: overrides.row_id || nextDraftId(),
  product_id: normalizeText(overrides.product_id),
  variant_id: normalizeText(overrides.variant_id),
  title: normalizeText(overrides.title || overrides.name),
  variant_title: normalizeText(overrides.variant_title),
  sku: normalizeText(overrides.sku),
  quantity: normalizeDraftNumberInput(overrides.quantity || 1) || '1',
  unit_weight: normalizeDraftNumberInput(overrides.unit_weight ?? overrides.weight),
  unit_price: normalizeDraftNumberInput(overrides.unit_price ?? overrides.price),
});

export const hydrateDeliveryItemDrafts = (items) => {
  if (!Array.isArray(items) || !items.length) {
    return [createEmptyDeliveryItem()];
  }
  const drafts = items
    .filter((item) => item && typeof item === 'object')
    .map((item) => createEmptyDeliveryItem(item));
  return drafts.length ? drafts : [createEmptyDeliveryItem()];
};

export const isMeaningfulDeliveryItem = (item) => {
  if (!item || typeof item !== 'object') {
    return false;
  }
  return Boolean(
    normalizeText(item.title)
    || normalizeText(item.variant_title)
    || normalizeText(item.sku)
    || normalizeText(item.product_id)
    || normalizeText(item.variant_id)
    || normalizeText(item.unit_weight)
    || normalizeText(item.unit_price)
    || parseQuantity(item.quantity) > 1
  );
};

export const buildDeliveryItemsSummary = (items) => {
  const drafts = Array.isArray(items) ? items : [];
  const rows = drafts.map((item) => {
    const quantity = parseQuantity(item?.quantity);
    const unitWeight = parsePositiveNumber(item?.unit_weight);
    const unitPrice = parsePositiveNumber(item?.unit_price);
    const totalWeight = unitWeight === null ? null : roundWeight(unitWeight * quantity);
    const lineTotal = unitPrice === null ? 0 : roundMoney(unitPrice * quantity);
    const isMeaningful = isMeaningfulDeliveryItem(item);

    return {
      ...item,
      quantity,
      unitWeight,
      unitPrice,
      totalWeight,
      lineTotal,
      isMeaningful,
    };
  });

  const activeRows = rows.filter((row) => row.isMeaningful);
  const totalWeight = roundWeight(
    activeRows.reduce((sum, row) => sum + (row.totalWeight === null ? 0 : row.totalWeight), 0)
  );
  const totalDeclaredValue = roundMoney(
    activeRows.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0)
  );
  const missingWeightItemsCount = activeRows.filter((row) => row.totalWeight === null).length;

  return {
    rows,
    activeRows,
    distinctItemsCount: activeRows.length,
    totalUnitsCount: activeRows.reduce((sum, row) => sum + row.quantity, 0),
    totalWeight,
    totalDeclaredValue,
    missingWeightItemsCount,
    hasDetailedItems: activeRows.length > 0,
    hasCompleteWeightData: activeRows.length > 0 && missingWeightItemsCount === 0,
  };
};

export const buildDeliveryItemsPayload = (items) => {
  const { activeRows } = buildDeliveryItemsSummary(items);
  return activeRows.map((row) => {
    const payload = {
      title: normalizeText(row.title),
      quantity: row.quantity,
    };
    if (normalizeText(row.product_id)) {
      payload.product_id = normalizeText(row.product_id);
    }
    if (normalizeText(row.variant_id)) {
      payload.variant_id = normalizeText(row.variant_id);
    }
    if (normalizeText(row.variant_title)) {
      payload.variant_title = normalizeText(row.variant_title);
    }
    if (normalizeText(row.sku)) {
      payload.sku = normalizeText(row.sku);
    }
    if (row.unitWeight !== null) {
      payload.unit_weight = row.unitWeight;
    }
    if (row.unitPrice !== null) {
      payload.unit_price = row.unitPrice;
    }
    return payload;
  });
};
